import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useNavigation } from '@react-navigation/native';
import { belongsToHomeworkTab } from '../utils/workKind';
import { toAssessmentDetailItem } from '../utils/assessmentDetail';
import { useAuth } from '../context/AuthContext';
import { assessmentAPI } from '../services/api';
import { Logger } from '../utils/logger';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { Toast } from '../components/common/Toast';
import { WebPageShell } from '../components/common/WebPageShell';
import { Spacing } from '../theme/colors';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import type { AssessmentResultRow, StudentMastery } from '../types';

const LEVEL_COLOR_KEY: Record<string, 'success' | 'primary' | 'warning' | 'danger'> = {
  EXCEEDING: 'success',
  MEETING: 'primary',
  DEVELOPING: 'warning',
  BEGINNING: 'danger',
};

export function AssessmentResultsScreen({ isNested }: { isNested?: boolean }) {
  const { selectedStudent } = useAuth();
  const navigation = useNavigation<any>();

  /**
   * Opens an assigned assessment in the same detail screen homework uses, so a
   * hand-in type can actually be handed in.
   *
   * Mapped to the shape that screen expects and tagged source:'assessment', so
   * it talks to the assessment endpoints - the homework ones would answer
   * "Homework not found." for this id.
   */
  const openAssigned = (row: any) => {
    // Every fact the row carries - type, marks, timing, class, teacher, files -
    // goes through, so the detail screen can show the whole assessment.
    navigation.navigate('HomeworkDetail', { homework: toAssessmentDetailItem(row) });
  };
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<AssessmentResultRow[]>([]);
  /** Work SET but not marked yet - homework-type rows belong to the Homework
   *  tab instead, and the server decides which is which. */
  const [assigned, setAssigned] = useState<any[]>([]);
  const [mastery, setMastery] = useState<StudentMastery | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const loadData = useCallback(async () => {
    if (!selectedStudent) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Same window as the homework tab, so "what has been set" means the same
      // thing on both screens.
      const now = new Date();
      const iso = (d: Date) => d.toISOString().slice(0, 10);
      const start = new Date(now); start.setDate(now.getDate() - 30);
      const end = new Date(now); end.setDate(now.getDate() + 30);

      const [resultsRes, masteryRes, assignedRes] = await Promise.all([
        assessmentAPI.getStudentResults(selectedStudent.id),
        assessmentAPI.getStudentMastery(selectedStudent.id).catch(() => ({ data: null })),
        // A bonus section, not the point of the screen: a failure here must not
        // take the results below with it.
        selectedStudent.classId
          ? assessmentAPI
              .getAssignedByClass(selectedStudent.classId, iso(start), iso(end), selectedStudent.enrollmentId)
              .catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      // Homework belongs to the Homework tab, marks included - it was showing
      // in both places, so the same piece of work appeared twice.
      setResults((resultsRes.data ?? []).filter((row: any) => !belongsToHomeworkTab(row)));
      setMastery(masteryRes.data ?? null);
      setAssigned(
        ((assignedRes as any)?.data?.data ?? []).filter((row: any) => !belongsToHomeworkTab(row)),
      );
    } catch (err) {
      Logger.error('AssessmentResults', 'Load failed', err);
      setToastMessage('Failed to load assessment results');
      setToastVisible(true);
      setResults([]);
      setMastery(null);
      setAssigned([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStudent]);

  useEffect(() => { loadData(); }, [loadData]);

  const content = loading ? (
    <LoadingSpinner message="Loading assessment results..." />
  ) : (
    <ScrollView style={styles.scroll}>
      {/* WHAT HAS BEEN SET, above what has been marked - a family checks "is
          there a test coming" far more often than last term's grade. */}
      {assigned.length > 0 && (
        <>
          <View style={styles.toolbar}>
            <Text style={styles.sectionTitle}>Assigned</Text>
          </View>
          <ScrollView horizontal={isMobile} showsHorizontalScrollIndicator={isMobile} style={isMobile ? styles.tableScroll : undefined}>
            <View style={[styles.tableCard, isMobile && styles.tableCardMobile]}>
              <View style={[styles.tableRow, styles.tableHead]}>
                <Text style={[styles.headText, { flex: 2.4 }]}>ASSESSMENT</Text>
                <Text style={[styles.headText, { flex: 1.4 }]}>SUBJECT</Text>
                <Text style={[styles.headText, { flex: 1.2 }]}>TYPE</Text>
                <Text style={[styles.headText, styles.centerCell]}>WHEN</Text>
                <Text style={[styles.headText, styles.centerCell]}>OUT OF</Text>
              </View>
              {assigned.map((row: any) => {
                const start = row.scheduleStartDateTime ?? row.ScheduleStartDateTime;
                const when = start ?? row.dueDate ?? row.DueDate;
                return (
                  <TouchableOpacity
                    key={row.id ?? row.Id}
                    style={styles.tableRow}
                    activeOpacity={0.7}
                    onPress={() => openAssigned(row)}
                  >
                    <Text style={[styles.cellText, { flex: 2.4 }]}>{row.title ?? row.Title}</Text>
                    <Text style={[styles.cellText, { flex: 1.4 }]}>{(row.subjectName ?? row.SubjectName) || '—'}</Text>
                    <Text style={[styles.cellText, { flex: 1.2 }]}>{(row.categoryName ?? row.CategoryName) || '—'}</Text>
                    <Text style={[styles.cellText, styles.centerCell]}>
                      {when ? new Date(when).toLocaleDateString() : '—'}
                    </Text>
                    <Text style={[styles.cellText, styles.centerCell]}>
                      {row.outOfMarks ?? row.OutOfMarks ?? '—'}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </ScrollView>
        </>
      )}

      <View style={styles.toolbar}>
        <Text style={styles.sectionTitle}>Published results</Text>
      </View>

      {results.length > 0 ? (
        <ScrollView horizontal={isMobile} showsHorizontalScrollIndicator={isMobile} style={isMobile ? styles.tableScroll : undefined}>
        <View style={[styles.tableCard, isMobile && styles.tableCardMobile]}>
          <View style={[styles.tableRow, styles.tableHead]}>
            <Text style={[styles.headText, { flex: 2.4 }]}>ASSESSMENT</Text>
            <Text style={[styles.headText, { flex: 1.4 }]}>SUBJECT</Text>
            <Text style={[styles.headText, styles.centerCell]}>SCORE</Text>
            <Text style={[styles.headText, styles.centerCell]}>%</Text>
            <Text style={[styles.headText, styles.centerCell]}>GRADE</Text>
            <Text style={[styles.headText, styles.centerCell]}>CLASS AVG</Text>
            <Text style={[styles.headText, styles.centerCell]}>RANK</Text>
          </View>

          {results.map((row, idx) => {
            const scoreText = row.isExempt
              ? 'Exempt'
              : row.isAbsent
                ? 'Absent'
                : row.marksObtained != null
                  ? `${row.marksObtained}/${row.maxMarks}`
                  : '—';
            return (
              <View key={row.assessmentId} style={[styles.rowWrap, idx === results.length - 1 && { borderBottomWidth: 0 }]}>
                <View style={styles.tableRow}>
                  <View style={{ flex: 2.4 }}>
                    <Text style={styles.titleText}>{row.assessmentTitle}</Text>
                    {(row.categoryName || row.assessmentType) && (
                      <Text style={styles.metaText}>
                        {row.categoryName || row.assessmentType}
                        {row.publishedDate ? ` · ${new Date(row.publishedDate).toLocaleDateString()}` : ''}
                      </Text>
                    )}
                  </View>
                  <Text style={[styles.cellText, { flex: 1.4 }]}>{row.subjectName || '—'}</Text>
                  <Text style={[styles.cellText, styles.centerCell, styles.boldCell]}>{scoreText}</Text>
                  <Text style={[styles.cellText, styles.centerCell]}>{row.percentage != null ? `${row.percentage}%` : '—'}</Text>
                  <View style={[styles.centerCell, styles.gradeCellWrap]}>
                    {row.grade ? (
                      <View style={styles.gradeBadge}>
                        <Text style={styles.gradeBadgeText}>{row.grade}</Text>
                      </View>
                    ) : (
                      <Text style={styles.cellText}>—</Text>
                    )}
                  </View>
                  <Text style={[styles.cellText, styles.centerCell]}>{row.classAverage != null ? `${row.classAverage}%` : '—'}</Text>
                  <Text style={[styles.cellText, styles.centerCell]}>{row.rank != null ? `#${row.rank}` : '—'}</Text>
                </View>
                {(row.teacherFeedback || row.remarks) && (
                  <View style={styles.feedbackRow}>
                    <FeatherIcon name="message-circle" size={13} color={colors.textMuted} style={{ marginRight: 6, marginTop: 2 }} />
                    <Text style={styles.feedbackText}>{row.teacherFeedback || row.remarks}</Text>
                  </View>
                )}
              </View>
            );
          })}
        </View>
        </ScrollView>
      ) : (
        <View style={styles.emptyContainer}>
          <FeatherIcon name="file-text" size={48} color={colors.textMuted} style={{ marginBottom: 16 }} />
          <Text style={styles.emptyTitle}>No Results Yet</Text>
          <Text style={styles.emptyText}>
            {assigned.length > 0
              // With work listed above, the old wording read as if nothing had
              // been set at all.
              ? 'Marks for the assessments above will appear here once the teacher publishes them.'
              : 'Marks and grades will appear here once assessments are published.'}
          </Text>
        </View>
      )}

      {mastery && mastery.outcomes.length > 0 && (
        <View style={styles.masterySection}>
          <Text style={styles.sectionTitle}>Learning progress</Text>
          <View style={styles.masteryGrid}>
            {mastery.outcomes.map((outcome) => {
              const key = LEVEL_COLOR_KEY[outcome.masteryLevel] ?? 'primary';
              const badgeBg = key === 'success' ? colors.successLight : key === 'warning' ? colors.warningLight : key === 'danger' ? colors.dangerLight : colors.primaryLight;
              const badgeText = key === 'success' ? colors.success : key === 'warning' ? colors.warning : key === 'danger' ? colors.danger : colors.primary;
              return (
                <View key={outcome.learningOutcomeId} style={[styles.masteryCard, isMobile && styles.masteryCardMobile]}>
                  <Text style={styles.masteryOutcomeName}>{outcome.outcomeName || 'Outcome'}</Text>
                  <Text style={styles.masteryPercent}>{outcome.masteryPercent}%</Text>
                  <View style={[styles.masteryBadge, { backgroundColor: badgeBg }]}>
                    <Text style={[styles.masteryBadgeText, { color: badgeText }]}>{outcome.masteryLevel}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      )}

      <Toast
        visible={toastVisible}
        message={toastMessage}
        type="error"
        onDismiss={() => setToastVisible(false)}
      />
    </ScrollView>
  );

  if (isNested) {
    return content;
  }
  return <WebPageShell title="Assessments" contentStyle={{ maxWidth: 900 }}>{content}</WebPageShell>;
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  centered: { flex: 1, minHeight: 240, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 12, color: colors.textSecondary, fontSize: 14 },
  scroll: { flex: 1, paddingHorizontal: Spacing.md, paddingTop: Spacing.sm },

  toolbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: { fontSize: 16, fontWeight: '700', color: colors.textPrimary },

  tableScroll: { marginBottom: 24 },
  tableCard: {
    backgroundColor: colors.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
    marginBottom: 24,
  },
  tableCardMobile: {
    minWidth: 640,
    marginBottom: 0,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  tableHead: {
    backgroundColor: colors.background,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingVertical: 10,
  },
  headText: { fontSize: 11, fontWeight: '700', color: colors.textSecondary },
  rowWrap: { borderBottomWidth: 1, borderBottomColor: colors.border },
  centerCell: { flex: 0.9, textAlign: 'center' },
  gradeCellWrap: { alignItems: 'center', justifyContent: 'center' },
  cellText: { fontSize: 13, color: colors.textSecondary },
  boldCell: { fontWeight: '700', color: colors.textPrimary },
  titleText: { fontSize: 13.5, fontWeight: '600', color: colors.textPrimary },
  metaText: { fontSize: 11.5, color: colors.textMuted, marginTop: 2 },
  gradeBadge: {
    backgroundColor: colors.primaryLight,
    borderRadius: 6,
    paddingHorizontal: 9,
    paddingVertical: 3,
  },
  gradeBadgeText: { fontSize: 12, fontWeight: '700', color: colors.primary },

  feedbackRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingBottom: 10,
    marginTop: -4,
  },
  feedbackText: { flex: 1, fontSize: 12, color: colors.textSecondary, lineHeight: 17 },

  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    backgroundColor: colors.card,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 24,
  },
  emptyTitle: { fontSize: 18, fontWeight: '700', color: colors.textSecondary, marginBottom: 8 },
  emptyText: { fontSize: 14, color: colors.textSecondary, textAlign: 'center', paddingHorizontal: 32, lineHeight: 20 },

  masterySection: { marginBottom: 32 },
  masteryGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 12 },
  masteryCard: {
    flexBasis: '31%',
    minWidth: 200,
    backgroundColor: colors.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 14,
  },
  masteryCardMobile: {
    flexBasis: '100%',
    minWidth: 0,
  },
  masteryOutcomeName: { fontSize: 12, fontWeight: '600', color: colors.textSecondary, marginBottom: 6 },
  masteryPercent: { fontSize: 20, fontWeight: '700', color: colors.textPrimary, marginBottom: 8 },
  masteryBadge: { alignSelf: 'flex-start', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
  masteryBadgeText: { fontSize: 10, fontWeight: '700' },
});
